import React from 'react';
import { Activity, User, FileText, Stethoscope, Clipboard, HeartPulse } from 'lucide-react';

const Skeleton = ({ className, children, ...props }) => {
  return (
    <div
      className={`relative overflow-hidden bg-slate-100 rounded-md ${className}`}
      {...props}
    >
      {/* Soft Shimmer Overlay */}
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
      {children}
    </div>
  );
};

export const CardSkeleton = () => (
  <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-4">
    <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-200">
            <HeartPulse size={20} />
        </Skeleton>
        <Skeleton className="h-6 w-1/3" />
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
    </div>
  </div>
);

export const TableSkeleton = ({ rows = 5 }) => (
  <div className="w-full space-y-4">
    <div className="flex space-x-4 border-b border-slate-50 pb-4">
      {Array(5).fill(0).map((_, i) => (
        <Skeleton key={i} className="h-5 flex-1" />
      ))}
    </div>
    {Array(rows).fill(0).map((_, i) => (
      <div key={i} className="flex space-x-4 py-3">
        {Array(5).fill(0).map((_, j) => (
          <Skeleton key={j} className="h-4 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

export const PatientDetailsSkeleton = () => (
    <div className="bg-white rounded-[2rem] shadow-xl p-8 border border-slate-100 space-y-8">
        <div className="flex items-center gap-4">
            <Skeleton className="w-14 h-14 rounded-2xl flex items-center justify-center text-slate-200">
                <User size={28} />
            </Skeleton>
            <div className="space-y-2 flex-1">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-3 w-1/4" />
            </div>
        </div>
        
        <div className="grid grid-cols-1 gap-6">
            <div className="space-y-4">
                {Array(2).fill(0).map((_, i) => (
                    <div key={i} className="space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-10 w-full rounded-2xl" />
                    </div>
                ))}
            </div>
        </div>

        <div className="pt-8 border-t border-slate-50">
            <div className="flex items-center gap-2 mb-6">
                <Activity className="w-4 h-4 text-blue-300" />
                <Skeleton className="h-4 w-40" />
            </div>
            <div className="space-y-4">
                {Array(2).fill(0).map((_, i) => (
                    <div key={i} className="space-y-4 p-4 bg-slate-50/50 rounded-3xl border border-slate-100">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-3 w-3 rounded-full" />
                            <Skeleton className="h-3 w-24" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Skeleton className="aspect-[4/3] rounded-2xl" />
                            <Skeleton className="aspect-[4/3] rounded-2xl" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

export const WorkflowActionSkeleton = () => (
    <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 border border-slate-100 min-h-[500px] space-y-10">
        <div className="flex items-center justify-between border-b border-slate-50 pb-5">
             <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-xl flex items-center justify-center text-blue-200">
                    <Clipboard size={20} />
                </Skeleton>
                <Skeleton className="h-6 w-56" />
             </div>
             <Skeleton className="h-8 w-32 rounded-full" />
        </div>
        
        <div className="space-y-8">
            <Skeleton className="h-36 w-full rounded-[2rem] flex items-center justify-center">
                <HeartPulse size={48} className="text-slate-100 opacity-50" />
            </Skeleton>
            
            <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-14 w-full rounded-2xl" />
                    <Skeleton className="h-14 w-full rounded-2xl" />
                </div>
                <div className="space-y-3">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-32 w-full rounded-2xl" />
                </div>
                <Skeleton className="h-12 w-full rounded-xl" />
            </div>
        </div>
    </div>
);

export const VitalsSkeleton = () => (
    <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-xl space-y-8">
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-xl flex items-center justify-center text-red-200">
                    <Activity size={20} />
                </Skeleton>
                <Skeleton className="h-6 w-32" />
            </div>
            <Skeleton className="h-4 w-24" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Array(6).fill(0).map((_, i) => (
                <div key={i} className="space-y-3 p-5 bg-slate-50/50 rounded-3xl border border-slate-100">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-8 w-full rounded-lg" />
                </div>
            ))}
        </div>
    </div>
);

export default Skeleton;
